/**
 * MODULE: _shared/lib/frontend/api/noteApi.ts
 * PURPOSE: Notes API client - CRUD operations for note management via data-management-service
 * 
 * DEPENDENCIES:
 *  - axios: HTTP client
 *  - ./config: API_CONFIG, getAuthHeaders
 * 
 * PUBLIC API:
 *  - Note: TypeScript interface for note records
 *  - noteApi.list(params): List notes with filters
 *  - noteApi.get(noteId): Get single note
 *  - noteApi.create(note): Create new note
 *  - noteApi.update(noteId, updates): Update note
 *  - noteApi.delete(noteId): Delete note
 * 
 * SIDE EFFECTS:
 *  - HTTP requests to /api/v1/notes endpoints
 *  - Reads JWT token from localStorage
 * 
 * AGENT SAFETY: RESTRICTED
 * FILE SIZE: 80 / 150 lines
 * 
 * See: /README.md#documentation | /ARCHITECTURE.md | /_shared/lib/frontend/api/README.md
 */

/**
 * Notes Widget API Integration
 * Connects to data-management-service via API Gateway
 * Consolidated service (Jan 2026): task, contact, note, file, metric, notification, search
 */

import axios from 'axios';
import { API_CONFIG, getAuthHeaders } from './config';

const API_BASE = API_CONFIG.baseURL;

export interface Note {
  id: string;
  tenant_id: string;
  user_id: string;
  title?: string;
  content: string;
  tags: string[];
  is_pinned: boolean;
  folder?: string;
  color?: string;
  created_by?: string;
  created_by_ai: boolean;
  ai_context?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateNoteRequest {
  title?: string;
  content: string;
  tags?: string[];
  is_pinned?: boolean;
  folder?: string;
  color?: string;
  created_by_ai?: boolean;
  ai_context?: string;
}

// Using centralized getAuthHeaders from config.ts

export const noteApi = {
  // List notes
  list: async (params?: {
    folder?: string;
    tag?: string;
    is_pinned?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<Note[]> => {
    const response = await axios.get(`${API_BASE}/api/v1/notes`, {
      params,
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Get single note
  get: async (noteId: string): Promise<Note> => {
    const response = await axios.get(`${API_BASE}/api/v1/notes/${noteId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Create note
  create: async (note: CreateNoteRequest): Promise<Note> => {
    const response = await axios.post(
      `${API_BASE}/api/v1/notes`,
      note,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Update note
  update: async (noteId: string, updates: Partial<CreateNoteRequest>): Promise<Note> => {
    const response = await axios.put(
      `${API_BASE}/api/v1/notes/${noteId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Delete note
  delete: async (noteId: string): Promise<void> => {
    await axios.delete(`${API_BASE}/api/v1/notes/${noteId}`, {
      headers: getAuthHeaders()
    });
  }
};

